import { createApi } from '@reduxjs/toolkit/query/react';

import { Exercise as ExercisePath } from '../endpoints';
import { IExercise } from '../../../types/interfaces/exercise';

import {
  getBaseQuery,
  getListProvidesTags,
  getUrlParamsString,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import { REDUX_CACHE, EXERCISE_PAGES, HTTP_METHOD } from '../constants';
import { INewFile } from '../../../types/interfaces/new-file';
import { orderBy } from '../../../utils/array-utils';
import { ExerciseScope } from '../../../shared/constants';

type AdminExercisesLazyQueryArgs = {
  searchValue?: string;
  pageSize: number;
  page: number;
  isPublic?: boolean;
  scope?: ExerciseScope;
  hasTitleTranslation?: boolean;
  hasDescriptionTranslation?: boolean;
};

const buildAdminExercisesLazyParams = ({
  searchValue,
  pageSize,
  page,
  isPublic,
  scope,
  hasTitleTranslation,
  hasDescriptionTranslation,
}: AdminExercisesLazyQueryArgs): Record<string, string | number> => ({
  searchValue: searchValue ?? '',
  pageSize,
  page,
  ...(typeof isPublic === 'boolean' ? { isPublic: String(isPublic) } : {}),
  ...(scope ? { scope } : {}),
  ...(typeof hasTitleTranslation === 'boolean'
    ? { hasTitleTranslation: String(hasTitleTranslation) }
    : {}),
  ...(typeof hasDescriptionTranslation === 'boolean'
    ? { hasDescriptionTranslation: String(hasDescriptionTranslation) }
    : {}),
});

export const exercisesApi = createApi({
  reducerPath: 'exercisesApi',
  tagTypes: [
    EXERCISE_PAGES.APPROVE_REQUESTED,
    EXERCISE_PAGES.ALL,
    EXERCISE_PAGES.PUBLIC,
    EXERCISE_PAGES.SELECTED,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getAllExercisesApi: builder.query<IExercise[], AdminExercisesLazyQueryArgs>(
      {
        keepUnusedDataFor: 3600,
        query(queryArgs) {
          const url =
            ExercisePath.ROOT +
            '?' +
            getUrlParamsString(buildAdminExercisesLazyParams(queryArgs));

          return {
            url,
            method: HTTP_METHOD.GET,
          };
        },
        providesTags: (result) =>
          getListProvidesTags(result, EXERCISE_PAGES.ALL),
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
          return `${endpointName}-${JSON.stringify(queryArgs)}`;
        },
        ...TransformedErrorResponseWithNotification(),
      },
    ),
    getExerciseByIdApi: builder.query<IExercise, string>({
      keepUnusedDataFor: 3600,
      query(id) {
        return {
          url: ExercisePath.BY_ID(id),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [{ type: EXERCISE_PAGES.SELECTED, id }];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getApproveRequestedExercisesApi: builder.query<
      IExercise[],
      { searchValue?: string; pageSize: number; page: number }
    >({
      keepUnusedDataFor: 3600,
      query({ searchValue, pageSize, page }) {
        const url =
          ExercisePath.APPROVE_REQUESTED +
          '?' +
          getUrlParamsString({
            searchValue: searchValue ?? '',
            pageSize,
            page,
          });

        return {
          url,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, EXERCISE_PAGES.APPROVE_REQUESTED),
      ...TransformedErrorResponseWithNotification(),
    }),
    getPublicExercisesApi: builder.query<IExercise[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: ExercisePath.PUBLIC,
          method: HTTP_METHOD.GET,
        };
      },
      transformResponse: (response: IExercise[]) => orderBy(response, 'title'),
      providesTags: (result) =>
        getListProvidesTags(result, EXERCISE_PAGES.PUBLIC),
      ...TransformedErrorResponseWithNotification(),
    }),
    exercisePublicApproveApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: ExercisePath.PUBLIC_APPROVE(id),
          method: HTTP_METHOD.PUT,
        };
      },
      invalidatesTags: (result, error, id) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_PAGES.ALL,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.SELECTED,
            },
            {
              type: EXERCISE_PAGES.PUBLIC,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.APPROVE_REQUESTED,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Exercise'),
    }),
    exercisePublicRejectApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: ExercisePath.PUBLIC_REJECT(id),
          method: HTTP_METHOD.PUT,
        };
      },
      invalidatesTags: (result, error, id) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_PAGES.SELECTED,
            },
            {
              type: EXERCISE_PAGES.ALL,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.PUBLIC,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.APPROVE_REQUESTED,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Exercise'),
    }),
    postPutExerciseApi: builder.mutation<
      void,
      {
        id: string;
        body: {
          exercise: Partial<
            IExercise & {
              avatarFileId: string | number;
              additionalEquipmentIds?: string[];
              additionalMuscleIds?: string[];
              developedSkillIds?: string[];
            }
          >;
          filesToUpload?: INewFile[];
          fileIdsToDelete?: string[];
        };
      }
    >({
      query({ id, body }) {
        return {
          url: ExercisePath.ROOT + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_PAGES.SELECTED,
            },
            {
              type: EXERCISE_PAGES.ALL,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.PUBLIC,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.APPROVE_REQUESTED,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Exercise'),
    }),
    deleteExerciseApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: `${ExercisePath.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, id) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_PAGES.ALL,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.PUBLIC,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.APPROVE_REQUESTED,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Exercise'),
    }),
  }),
});

export const {
  useLazyGetExerciseByIdApiQuery: useLazyGetExerciseById,
  useGetPublicExercisesApiQuery: useGetPublicExercises,
  useGetExerciseByIdApiQuery: useGetExerciseById,
  useDeleteExerciseApiMutation: useDeleteExercise,
  useGetAllExercisesApiQuery: useGetAllExercises,
  useLazyGetAllExercisesApiQuery: useLazyGetAllExercises,
  useGetApproveRequestedExercisesApiQuery: useGetApproveRequestedExercises,
  useLazyGetApproveRequestedExercisesApiQuery:
    useLazyGetApproveRequestedExercises,
  useExercisePublicApproveApiMutation: useExercisePublicApprove,
  useExercisePublicRejectApiMutation: useExercisePublicReject,
  usePostPutExerciseApiMutation: usePostPutExercise,
} = exercisesApi;
